import React, { useEffect, useState } from 'react';
import { Badge, Drawer, Dropdown, Grid } from 'antd';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@shared/hooks/useAuthStore';
import { hasNavAccess } from '@shared/utils/roleAccess';
import { useWatchlist } from '@shared/api/hooks/useWatchlist';
import { brand } from '@/theme/tokens';
import { GlobalSearch } from './GlobalSearch';


interface NavItem {
  key: string;
  path: string;
  labelKey: string;
  icon: string;
  /** 'live' = badge pulsante LIVE; 'watchlist' = badge numerico utenti flaggati */
  badge?: 'live' | 'watchlist';
  /** Se presente, l'item è visibile solo a chi ha uno di questi ruoli (oltre a SUPER_ADMIN). */
  roles?: string[];
}

interface NavSection {
  key: string;
  labelKey: string;
  items: NavItem[];
}

const ICON_MAP: Record<string, string> = {
  dashboard: '▦',
  matches: '◉',
  standings: '≡',
  'ticket-stats': '▧',
  roster: '◈',
  orders: '▣',
  products: '▤',
  'shop-settings': '⚙',
  inventory: '▤',
  coupons: '◇',
  discounts: '◈',
  loyalty: '★',
  badges: '★',
  leaderboard: '★',
  sponsors: '◆',
  partners: '◑',
  news: '▥',
  'rss-import': '⇊',
  polls: '◐',
  push: '▲',
  fans: '◎',
  'season-tickets': '▦',
  receipts: '❐',
  'receipt-inspector': '⊕',
  watchlist: '⚑',
  checkin: '✓',
  analytics: '◭',
  'analytics-audience': '◔',
  'analytics-store': '▨',
  'analytics-loyalty': '❖',
  'analytics-sponsor': '✪',
  staff: '☰',
  audit: '☰',
  settings: '◉',
  'payment-settings': '◇',
  'email-templates': '▥',
  'legal-pages': '§',
  'ai-settings': '✦',
  'hero-banners': '▥',
  'media-library': '▥',
  'banned-words': '◐',
  tenants: '▦',
};

// T-211 — ogni voce dichiara i ruoli SPECIALISTI che la vedono; Admin e Developer
// li includono via gerarchia (vedi hasNavAccess). Il backend resta la barriera vera.
const NAV: NavSection[] = [
  {
    key: 'overview',
    labelKey: 'nav.section.overview',
    items: [
      { key: 'dashboard', path: '/', labelKey: 'nav.dashboard', icon: ICON_MAP['dashboard'], roles: ['CLUB_ADMIN'] },
    ],
  },
  {
    key: 'sport',
    labelKey: 'nav.section.sport',
    items: [
      { key: 'matches', path: '/matches', labelKey: 'nav.matches', icon: ICON_MAP['matches'], badge: 'live', roles: ['CLUB_EDITOR'] },
      { key: 'standings', path: '/standings', labelKey: 'nav.standings', icon: ICON_MAP['standings'], roles: ['CLUB_EDITOR'] },
      { key: 'ticket-stats', path: '/ticket-stats', labelKey: 'nav.ticketStats', icon: ICON_MAP['ticket-stats'], roles: ['CLUB_ADMIN'] },
      { key: 'roster', path: '/roster', labelKey: 'nav.roster', icon: ICON_MAP['roster'], roles: ['CLUB_EDITOR'] },
    ],
  },
  {
    key: 'commerce',
    labelKey: 'nav.section.commerce',
    items: [
      { key: 'orders', path: '/orders', labelKey: 'nav.orders', icon: ICON_MAP['orders'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      { key: 'products', path: '/products', labelKey: 'nav.products', icon: ICON_MAP['products'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      { key: 'shop-settings', path: '/shop-settings', labelKey: 'nav.shopSettings', icon: ICON_MAP['shop-settings'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      { key: 'inventory', path: '/inventory', labelKey: 'nav.inventory', icon: ICON_MAP['inventory'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      { key: 'coupons', path: '/coupons', labelKey: 'nav.coupons', icon: ICON_MAP['coupons'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      { key: 'discounts', path: '/discounts', labelKey: 'nav.discounts', icon: ICON_MAP['discounts'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      { key: 'partners', path: '/partners', labelKey: 'nav.partners', icon: ICON_MAP['partners'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
      // Sponsor spostati in COMMERCIALE (decisione umano 17/09): Shop Manager + Supporto.
      { key: 'sponsors', path: '/sponsors', labelKey: 'nav.sponsors', icon: ICON_MAP['sponsors'], roles: ['CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'] },
    ],
  },
  {
    key: 'loyalty',
    labelKey: 'nav.section.loyalty',
    items: [
      { key: 'loyalty', path: '/loyalty', labelKey: 'nav.loyalty', icon: ICON_MAP['loyalty'], roles: ['CLUB_SUPPORT'] },
      {
        key: 'loyalty-periods',
        path: '/loyalty-periods',
        labelKey: 'nav.loyaltyPeriods',
        icon: ICON_MAP['loyalty'],
        roles: ['CLUB_SUPPORT'],
      },
      { key: 'badges', path: '/badges', labelKey: 'nav.badges', icon: ICON_MAP['badges'], roles: ['CLUB_SUPPORT'] },
      { key: 'leaderboard', path: '/leaderboard', labelKey: 'nav.leaderboard', icon: ICON_MAP['leaderboard'], roles: ['CLUB_SUPPORT'] },
      { key: 'receipts', path: '/receipts', labelKey: 'nav.receipts', icon: ICON_MAP['receipts'], roles: ['CLUB_SUPPORT'] },
      { key: 'receipt-inspector', path: '/receipt-inspector', labelKey: 'nav.receiptInspector', icon: ICON_MAP['receipt-inspector'], roles: ['CLUB_SUPPORT'] },
      // Check-in: Supporto + Editor (operazione del giorno partita).
      { key: 'checkin', path: '/checkin', labelKey: 'nav.checkin', icon: ICON_MAP['checkin'], roles: ['CLUB_SUPPORT', 'CLUB_EDITOR'] },
    ],
  },
  {
    key: 'content',
    labelKey: 'nav.section.content',
    items: [
      { key: 'news', path: '/news', labelKey: 'nav.news', icon: ICON_MAP['news'], roles: ['CLUB_EDITOR'] },
      { key: 'rss-import', path: '/rss-import', labelKey: 'nav.rssImport', icon: ICON_MAP['rss-import'], roles: ['CLUB_EDITOR'] },
      { key: 'polls', path: '/polls', labelKey: 'nav.polls', icon: ICON_MAP['polls'], roles: ['CLUB_EDITOR'] },
      { key: 'push', path: '/push', labelKey: 'nav.push', icon: ICON_MAP['push'], roles: ['CLUB_EDITOR'] },
      { key: 'hero-banners', path: '/hero-banners', labelKey: 'nav.heroBanners', icon: ICON_MAP['hero-banners'], roles: ['CLUB_EDITOR'] },
      { key: 'media-library', path: '/media-library', labelKey: 'nav.mediaLibrary', icon: ICON_MAP['media-library'], roles: ['CLUB_EDITOR'] },
    ],
  },
  {
    key: 'tifosi',
    labelKey: 'nav.section.tifosi',
    items: [
      { key: 'fans', path: '/fans', labelKey: 'nav.fans', icon: ICON_MAP['fans'], roles: ['CLUB_SUPPORT'] },
      {
        key: 'season-tickets',
        path: '/season-tickets',
        labelKey: 'nav.seasonTickets',
        icon: ICON_MAP['season-tickets'],
        roles: ['CLUB_ADMIN', 'DEVELOPER', 'CLUB_SUPPORT'],
      },
      { key: 'banned-words', path: '/banned-words', labelKey: 'nav.bannedWords', icon: ICON_MAP['banned-words'], roles: ['CLUB_ADMIN'] },
    ],
  },
  {
    key: 'support',
    labelKey: 'nav.section.support',
    items: [
      { key: 'watchlist', path: '/watchlist', labelKey: 'nav.watchlist', icon: ICON_MAP['watchlist'], badge: 'watchlist', roles: ['CLUB_SUPPORT'] },
    ],
  },
  {
    key: 'analytics',
    labelKey: 'nav.section.analytics',
    items: [
      { key: 'analytics-audience', path: '/analytics/audience', labelKey: 'nav.analyticsAudience', icon: ICON_MAP['analytics-audience'], roles: ['CLUB_ADMIN'] },
      { key: 'analytics-store', path: '/analytics/store', labelKey: 'nav.analyticsStore', icon: ICON_MAP['analytics-store'], roles: ['CLUB_ADMIN'] },
      { key: 'analytics-loyalty', path: '/analytics/loyalty', labelKey: 'nav.analyticsLoyalty', icon: ICON_MAP['analytics-loyalty'], roles: ['CLUB_ADMIN'] },
      { key: 'analytics-sponsor', path: '/analytics/sponsors', labelKey: 'nav.analyticsSponsor', icon: ICON_MAP['analytics-sponsor'], roles: ['CLUB_ADMIN'] },
    ],
  },
  {
    key: 'system',
    labelKey: 'nav.section.system',
    items: [
      { key: 'staff', path: '/staff', labelKey: 'nav.staff', icon: ICON_MAP['staff'], roles: ['CLUB_ADMIN'] },
      { key: 'audit', path: '/audit', labelKey: 'nav.audit', icon: ICON_MAP['audit'], roles: ['CLUB_ADMIN'] },
    ],
  },
  {
    key: 'config',
    labelKey: 'nav.section.config',
    items: [
      { key: 'settings', path: '/settings', labelKey: 'nav.settings', icon: ICON_MAP['settings'], roles: ['CLUB_ADMIN'] },
      { key: 'payment-settings', path: '/payment-settings', labelKey: 'nav.paymentSettings', icon: ICON_MAP['payment-settings'], roles: ['CLUB_ADMIN'] },
      { key: 'email-templates', path: '/settings/email-templates', labelKey: 'nav.emailTemplates', icon: ICON_MAP['email-templates'], roles: ['CLUB_ADMIN'] },
      { key: 'legal-pages', path: '/settings/legal-pages', labelKey: 'nav.legalPages', icon: ICON_MAP['legal-pages'], roles: ['CLUB_ADMIN'] },
      { key: 'ai-settings', path: '/settings/ai', labelKey: 'nav.aiSettings', icon: ICON_MAP['ai-settings'], roles: ['DEVELOPER'] },
    ],
  },
  {
    key: 'superadmin',
    labelKey: 'nav.section.superadmin',
    items: [
      { key: 'tenants', path: '/admin/tenants', labelKey: 'nav.tenants', icon: ICON_MAP['tenants'] },
    ],
  },
];

const livePulseStyle = `
@keyframes blLivePulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}
`;

function BOTopbar({
  user,
  logout,
  isMobile,
  onOpenMenu,
}: {
  user: import('@shared/hooks/useAuthStore').AuthUser | null;
  logout: () => void;
  isMobile: boolean;
  onOpenMenu: () => void;
}) {
  const { t } = useTranslation();
  const initials = (() => {
    const name = user?.displayName ?? user?.email ?? '';
    return name.slice(0, 2).toUpperCase();
  })();

  const dropdownItems = [
    {
      key: 'logout',
      label: t('action.logout'),
      onClick: () => logout(),
    },
  ];

  // T-194: etichetta ambiente guidata da una variabile di build esplicita.
  // Default (variabile assente o qualsiasi valore != 'staging') = PRODUZIONE,
  // così il build di prod resta invariato. Solo VITE_APP_ENV=staging mostra STAGING.
  const envLabel = import.meta.env.VITE_APP_ENV === 'staging' ? 'STAGING' : 'PRODUZIONE';

  return (
    <div
      style={{
        background: '#0b0b0d',
        height: 56,
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {/* T-213A — hamburger: sotto md apre il menu di navigazione a scomparsa. */}
      {isMobile && (
        <button
          type="button"
          aria-label={t('nav.openMenu')}
          onClick={onOpenMenu}
          style={{
            background: 'transparent',
            border: 0,
            borderRight: '1px solid rgba(255,255,255,0.08)',
            color: '#fff',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 20,
            lineHeight: 1,
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          ☰
        </button>
      )}
      <div
        style={{
          minWidth: 0,
          width: isMobile ? 'auto' : 260,
          borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: isMobile ? '0 12px' : '0 18px',
          flexShrink: isMobile ? 1 : 0,
          overflow: 'hidden',
        }}
      >
        <img
          src="/assets/diavolo-rosso.png"
          style={{ height: 28, width: 28, objectFit: 'contain', flexShrink: 0 }}
          alt="logo"
        />
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 900,
              fontStyle: 'italic',
              letterSpacing: -0.3,
              color: '#fff',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {isMobile ? 'TERAMO 1913' : 'TERAMO CALCIO 1913'}
          </div>
          {!isMobile && (
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: 2,
                color: brand.primary,
                marginTop: 3,
              }}
            >
              BACKOFFICE · v1.0
            </div>
          )}
        </div>
      </div>

      {/* Ricerca globale: nascosta sotto md per far stare la topbar senza scroll. */}
      {!isMobile && (
        <div
          style={{
            flex: 1,
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <GlobalSearch />
        </div>
      )}
      {isMobile && <div style={{ flex: 1, minWidth: 0 }} />}

      {/* Stato API / ambiente: solo da md in su (occupa troppo su mobile). */}
      {!isMobile && (
      <div
        style={{
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '0 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: brand.win,
              boxShadow: `0 0 6px ${brand.win}`,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: '#fff',
            }}
          >
            API · OK
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
            Ambiente
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: brand.gold,
            }}
          >
            {envLabel}
          </span>
        </div>
      </div>
      )}

      {!isMobile && (
      <button
        style={{
          background: 'transparent',
          border: 0,
          color: '#fff',
          position: 'relative',
          padding: '0 16px',
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          fontFamily: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 16 }}>▲</span>
      </button>
      )}

      <div
        style={{
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          flexShrink: 0,
        }}
      >
        <Dropdown menu={{ items: dropdownItems }} trigger={['click']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 4,
                background: brand.primary,
                color: '#fff',
                fontSize: 11,
                fontWeight: 900,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            {!isMobile && (
              <div style={{ lineHeight: 1 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#fff',
                    marginBottom: 2,
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.displayName ?? user?.email}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.55)',
                    letterSpacing: 0.5,
                  }}
                >
                  {user?.role}
                </div>
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
}

function BOSidebar({
  collapsed,
  toggleCollapse,
  activeKey,
  visibleSections,
  variant = 'sider',
  onNavigate,
}: {
  collapsed: boolean;
  toggleCollapse: () => void;
  activeKey: string;
  visibleSections: NavSection[];
  /** 'sider' = colonna fissa a sinistra; 'drawer' = contenuto del menu a scomparsa (mobile). */
  variant?: 'sider' | 'drawer';
  /** Chiamato quando si sceglie una voce (nel drawer chiude il menu). */
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  // Badge numerico watchlist — aggiornato automaticamente su focus finestra
  const { data: watchlistData } = useWatchlist();
  const watchlistCount = watchlistData?.counts.flaggedUsers ?? 0;
  const isDrawer = variant === 'drawer';

  return (
    <div
      style={{
        width: isDrawer ? '100%' : collapsed ? 56 : 220,
        height: '100%',
        background: '#fff',
        borderRight: isDrawer ? 'none' : `1px solid ${brand.line}`,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.15s ease',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {visibleSections.map((section) => (
          <div key={section.key}>
            {!collapsed && (
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 900,
                  letterSpacing: 1.5,
                  color: brand.muted2,
                  padding: '10px 18px 6px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {t(section.labelKey)}
              </div>
            )}
            {section.items.map((item) => {
              const isActive = activeKey === item.key;
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: collapsed ? '9px 0' : '8px 18px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    background: isActive ? brand.paper2 : 'transparent',
                    border: 'none',
                    borderLeft: `3px solid ${isActive ? brand.ink : 'transparent'}`,
                    cursor: 'pointer',
                    color: brand.inkSoft,
                    textDecoration: 'none',
                    position: 'relative',
                    boxSizing: 'border-box',
                  }}
                >
                  {/* Icona — con badge numerico watchlist quando collapsed */}
                  <Badge
                    count={item.badge === 'watchlist' && collapsed ? watchlistCount : 0}
                    size="small"
                    color={brand.danger}
                    offset={[2, -2]}
                    style={{ flexShrink: 0 }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        width: 18,
                        textAlign: 'center',
                        color: isActive ? brand.primary : brand.muted,
                        flexShrink: 0,
                        display: 'inline-block',
                      }}
                    >
                      {item.icon}
                    </span>
                  </Badge>
                  {!collapsed && (
                    <>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: isActive ? 800 : 600,
                          letterSpacing: -0.1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t(item.labelKey)}
                      </span>
                      {item.badge === 'live' && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#fde8e6',
                            color: brand.primary,
                            fontSize: 8,
                            fontWeight: 900,
                            padding: '2px 6px',
                            borderRadius: 3,
                            letterSpacing: 0.6,
                            marginLeft: 'auto',
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: brand.primary,
                              animation: 'blLivePulse 1.3s infinite',
                              flexShrink: 0,
                            }}
                          />
                          LIVE
                        </span>
                      )}
                      {/* Badge numerico watchlist — solo sidebar espansa */}
                      {item.badge === 'watchlist' && watchlistCount > 0 && (
                        <Badge
                          count={watchlistCount}
                          size="small"
                          color={brand.danger}
                          style={{ marginLeft: 'auto' }}
                        />
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {!isDrawer && (
        <button
          onClick={toggleCollapse}
          style={{
            border: 0,
            borderTop: `1px solid ${brand.line}`,
            background: '#fff',
            cursor: 'pointer',
            padding: 10,
            fontSize: 12,
            color: brand.muted,
            fontWeight: 700,
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {collapsed ? '▶' : '◀ Comprimi'}
        </button>
      )}
    </div>
  );
}

export function MainLayout(): React.ReactElement {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();

  // T-213A — sotto md (768px) il Sider fisso diventa un Drawer a scomparsa.
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Se si torna a schermo largo mentre il drawer è aperto, lo chiudiamo per
  // evitare un menu "fantasma" quando ricompare la colonna fissa.
  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  const activeKey =
    NAV.flatMap((s) => s.items)
      .filter((i) =>
        i.path === '/' ? location.pathname === '/' : location.pathname.startsWith(i.path),
      )
      .sort((a, b) => b.path.length - a.path.length)[0]?.key ?? 'dashboard';

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const visibleSections = NAV.filter((s) => s.key !== 'superadmin' || isSuperAdmin)
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => hasNavAccess(user?.role, i.roles)),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <>
      <style>{livePulseStyle}</style>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <BOTopbar
          user={user}
          logout={logout}
          isMobile={isMobile}
          onOpenMenu={() => setDrawerOpen(true)}
        />
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Desktop: colonna fissa. Mobile: nascosta, sostituita dal Drawer. */}
          {!isMobile && (
            <BOSidebar
              collapsed={collapsed}
              toggleCollapse={() => setCollapsed((c) => !c)}
              activeKey={activeKey}
              visibleSections={visibleSections}
            />
          )}
          <div style={{ flex: 1, overflowY: 'auto', background: brand.bg, minWidth: 0 }}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* T-213A — menu di navigazione a scomparsa (solo mobile). */}
      <Drawer
        placement="left"
        open={isMobile && drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={240}
        closable={false}
        styles={{ body: { padding: 0 }, header: { display: 'none' } }}
      >
        <BOSidebar
          collapsed={false}
          toggleCollapse={() => undefined}
          activeKey={activeKey}
          visibleSections={visibleSections}
          variant="drawer"
          onNavigate={() => setDrawerOpen(false)}
        />
      </Drawer>
    </>
  );
}
